import { useMemo } from "react"

import { useSelector } from "react-redux"

import { selectCalendarEvents } from "@selectors/calendar"
import { checkIfEventsConflict } from "@utils/calendarConflictUtils"

export const useGameplans = ({ teeup, type, isNewest }) => {
  const calendarEvents = useSelector(selectCalendarEvents)

  const sortSuggestions = suggestions => {
    return suggestions?.sort((a, b) => {
      const comparisonResult = isNewest ? b.id - a.id : a.id - b.id

      if (a.isWithdrawn === b.isWithdrawn) {
        return comparisonResult
      } else {
        return a.isWithdrawn ? 1 : -1
      }
    })
  }

  const getDecidedSuggestion = suggestions => {
    return suggestions?.find(
      ({ selected, isSelected, decided, isDecided }) =>
        selected || isSelected || decided || isDecided
    )
  }

  const plan = useMemo(() => {
    // FIX 1 (2026-07-04, detail first-paint error-boundary flash): `teeup` was destructured
    // straight off `teeup.gameplans` -- safe once TeeUpPage's `!teeup?.id` mount guard has
    // passed, but WhenWhereCard/useGameplans reads the SAME `state.activeTeeup` selector
    // independently via useTeeUpPage(), so any future caller (or an in-flight websocket/poll
    // dispatch that resets activeTeeup mid-render, see TeeUpPageContext's WB5 polling + socket
    // handlers) that lands here before `teeup` is populated throws
    // "Cannot read properties of undefined/null (reading 'gameplans')", caught by the app-shell
    // ErrorBoundary (App.js) as the "something went wrong" flash. Optional-chain the whole
    // access so an unpopulated/transitional teeup renders an empty gameplan instead of crashing.
    return teeup?.gameplans?.find(plan => plan.type === type)
  }, [teeup, type])

  // C1 fix (2026-07-03): the inbox worker (worker_checkCalendarConflicts.ts) only annotates
  // isCalendarConflict on the INBOX store's gameplanOptions -- the detail Game Plan tab reads
  // teeup.gameplans straight from activeTeeup state, which never receives that flag, so
  // row-level conflict labels had nothing to key off for logged-in users. Compute it here for
  // "when" suggestions only (cheap, memoized on plan/calendarEvents), checking every When
  // suggestion -- decided or pending -- against the user's synced calendar events (empty on dev
  // until the calendar-events endpoint ships, see C1/C2 data caveat) and against each other,
  // mirroring the worker's own logic via the shared checkIfEventsConflict predicate.
  const suggestionsWithConflicts = useMemo(() => {
    const suggestions = plan?.suggestions || []
    if (type !== "when" || suggestions.length === 0) return suggestions

    const calendarEventsValues = Object.values(calendarEvents || {})

    return suggestions.map(suggestion => {
      const conflictsWithSibling = suggestions.some(
        other => other.id !== suggestion.id && checkIfEventsConflict(suggestion, other)
      )
      const conflictsWithCalendar = calendarEventsValues.some(event =>
        checkIfEventsConflict(suggestion, event)
      )

      return {
        ...suggestion,
        isCalendarConflict:
          !!suggestion.isCalendarConflict ||
          conflictsWithSibling ||
          conflictsWithCalendar,
      }
    })
  }, [plan, type, calendarEvents])

  return useMemo(
    () => ({
      suggestions: sortSuggestions(suggestionsWithConflicts) || [],
      decidedSuggestion: getDecidedSuggestion(suggestionsWithConflicts),
    }),
    [suggestionsWithConflicts, isNewest]
  )
}
